import { formatInTimeZone, toZonedTime } from 'date-fns-tz'
import { addHours, set, isSameDay, format } from 'date-fns'
import {
  CalendarDots,
  SmileyAngry,
  SmileyMeh,
  Smiley,
  Star,
  ThumbsDown,
  ThumbsUp,
  DeviceMobile,
  EnvelopeSimple,
  ChatCircle,
} from '@phosphor-icons/react'
import ReactTooltip from 'react-tooltip'

import { getFullDateFromDayMinutes } from '../../helpers/helpers'
import DropdownDots from '../DropdownDots/DropdownDots'
import Avatar from '../../Avatar'

export const parseSurveyStartTime = (dayMinutes) => {
  if (dayMinutes == null) return ''
  const date = new Date(getFullDateFromDayMinutes(dayMinutes).valueOf())
  return isNaN(date.getTime()) ? '' : date
}

export const getSurveyStatuses = (t) => [
  {
    text: t('surveys:statuses.all'),
    value: 'all',
  },
  {
    text: t('surveys:statuses.scheduled'),
    value: 'scheduled',
  },
  {
    text: t('surveys:statuses.ongoing'),
    value: 'ongoing',
  },
  {
    text: t('surveys:statuses.completed'),
    value: 'completed',
  },
]

export const getSurveyType = (t) => [
  {
    id: 1,
    text: t('surveys:options.survey'),
    value: 'survey',
  },
]

export const getResponseTypeOptions = (t) => [
  {
    id: 0,
    label: t('surveys:options.smileyFaces'),
    value: 'smiley',
  },
  {
    id: 1,
    label: t('surveys:options.thumbsUpDown'),
    value: 'thumbs',
  },
  {
    id: 2,
    label: t('surveys:options.fivePointScale'),
    value: 'stars',
  },
]

export const smileyResponse = 'smiley'
export const thumbsResponse = 'thumbs'
export const starsResponse = 'stars'

// `value` stays untranslated: it's used as a grouping key (see transformSurveyData) and
// persisted as the survey `type`, not just display copy. `label` is display-only.
export const getSurveyTypeOptions = (t) => [
  {
    id: 0,
    label: t('surveys:options.satisfactionSurvey'),
    value: 'NPS',
  },
  {
    id: 1,
    label: t('surveys:options.singleChoice'),
    value: 'Single choice',
  },
  {
    id: 2,
    label: t('surveys:options.multipleChoice'),
    value: 'Multiple choice',
  },
]
export const satisfactionSurvey = 'NPS'
export const singleChoiceSurvey = 'Single choice'
export const multipleChoiceSurvey = 'Multiple choice'

export const getSurveyCommunicationTypeOptions = (t) => [
  {
    id: 0,
    label: t('surveys:options.inApp'),
    value: 'in-app',
  },
  {
    id: 1,
    label: t('surveys:options.email'),
    value: 'email',
  },
  {
    id: 2,
    label: t('surveys:options.sms'),
    value: 'sms',
  },
]
export const inAppCommunication = 'in-app'
export const emailCommunication = 'email'
export const smsCommunication = 'sms'
export const prepareCommunicationIcon = (communicationType) => {
  const size = 16
  switch (communicationType) {
    case inAppCommunication:
      return <DeviceMobile size={size} />
    case emailCommunication:
      return <EnvelopeSimple size={size} />
    case smsCommunication:
      return <ChatCircle size={size} />
    default:
      return null
  }
}
export const renderSelectPlaceholder = (t, options, noOptionsText) => {
  if (!options || !options.length) return t('surveys:form.selectFallback', { label: noOptionsText })
  return options.map((building) => building.text).join(', ')
}

export const getEmptyRecipentsPlaceholder = (t, recipentType) => {
  switch (recipentType) {
    case 'residents':
      return t('surveys:recipients.byNameAptEmailPhone')
    case 'groups':
      return t('surveys:recipients.byGroupName')
    case 'units':
      return t('surveys:recipients.byUnit')
    case 'clientTags':
      return t('surveys:recipients.byTagName')
  }
}

const getMilliseconds = (days) => days * 24 * 60 * 60 * 1000

export const getSurveyAnalyticsPeriods = (t) => [
  { value: getMilliseconds(7), label: t('surveys:options.lastWeek') },
  { value: getMilliseconds(30), label: t('surveys:options.last30Days') },
  { value: getMilliseconds(90), label: t('surveys:options.last90Days') },
  { value: getMilliseconds(365), label: t('surveys:options.lastYear') },
]

export const surveyColumns = ({
  t,
  timezone,
  showDetails,
  onEdit,
  onDelete,
  onStop,
  onResume,
  onCopy,
}) => {
  return [
    {
      Header: t('surveys:columns.messageTitle'),
      Cell: (data) => {
        const { name, isEventSurvey, id, isOneTime } = data.row.original

        const shouldDisplayTooltip = isEventSurvey || !isOneTime

        return (
          <div className="survey-name-cell">
            <p>{name}</p>
            {shouldDisplayTooltip ? (
              <div className="event-survey-wrap" data-tip data-for={`event-survey-tooltip-${id}`}>
                <ReactTooltip
                  className="tooltip"
                  id={`event-survey-tooltip-${id}`}
                  multiline
                  effect="solid"
                >
                  {t('surveys:columns.eventSurveyTooltip')}
                </ReactTooltip>
                <CalendarDots size={16} />
              </div>
            ) : null}
          </div>
        )
      },
    },
    {
      Header: t('surveys:columns.surveyType'),
      Cell: (data) => {
        const { type, subtype } = data.row.original
        return (
          <div>
            <strong>{type}</strong>
            {subtype ? <div>{subtype}</div> : null}
          </div>
        )
      },
    },
    {
      Header: t('surveys:columns.communicationType'),
      Cell: (data) => {
        const { row } = data
        return getSurveyCommunicationTypeOptions(t).find(
          (option) => option.value === row.original.communicationType,
        ).label
      },
    },
    {
      Header: t('surveys:columns.frequency'),
      Cell: (data) => {
        const { row } = data
        return row.original.isOneTime ? t('surveys:columns.oneTime') : t('surveys:columns.ongoing')
      },
    },
    {
      Header: t('surveys:columns.startEndDate'),
      Cell: (data) => {
        const { startDate, endDate, isOneTime } = data.row.original
        const preparedEndDate = endDate ? formatInTimeZone(endDate, timezone, 'MM/dd/yyyy') : ''

        return `${formatInTimeZone(startDate, timezone, 'MM/dd/yyyy')} - ${
          isOneTime ? preparedEndDate : t('surveys:columns.ongoing')
        }`
      },
    },
    {
      Header: t('surveys:columns.status'),
      Cell: (data) => {
        const { status } = data.row.original
        return <div className={`message-status-filter ${removeSpaces(status)}`}>{status}</div>
      },
    },
    {
      Header: ' ',
      Cell: (data) => {
        const { id, name, status } = data.row.original

        return (
          <DropdownDots
            options={[
              {
                id: 0,
                text: t('surveys:columns.details'),
                action: () => showDetails(id),
              },
              {
                id: 1,
                text: t('surveys:columns.edit'),
                action: status !== 'Completed' ? () => onEdit(id) : null,
              },
              {
                id: 2,
                text:
                  status === 'On hold'
                    ? t('surveys:columns.resumeSurvey')
                    : t('surveys:columns.stopSurvey'),
                action:
                  status !== 'Completed'
                    ? () => (status === 'On hold' ? onResume(id) : onStop(id))
                    : null,
              },
              {
                id: 3,
                text: t('surveys:columns.copy'),
                action: () => onCopy(id),
              },
              {
                id: 4,
                text: t('surveys:columns.delete'),
                action: () => onDelete({ id, name }),
              },
            ]}
          />
        )
      },
    },
  ]
}

export const transformSurveyData = (input) => {
  const npsData = {
    type: 'NPS',
    sentSurveys: { total: 0, subtype: {} },
    responses: { total: 0, subtype: {} },
    responseRate: { total: 0, subtype: {} },
  }

  const groupedData = {
    'Single choice': { type: 'Single choice', sentSurveys: 0, responses: 0, responseRate: 0 },
    'Multiple choice': { type: 'Multiple choice', sentSurveys: 0, responses: 0, responseRate: 0 },
  }

  const formatResponseRate = (value) => {
    return value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)
  }

  input.forEach(({ type, subtype, sentSurveys, responses }) => {
    if (type === 'NPS') {
      npsData.sentSurveys.total += sentSurveys
      npsData.responses.total += responses

      npsData.sentSurveys.subtype[subtype] = sentSurveys
      npsData.responses.subtype[subtype] = responses
      const rate = sentSurveys > 0 ? (responses / sentSurveys) * 100 : 0
      npsData.responseRate.subtype[subtype] = formatResponseRate(rate)
    } else if (type in groupedData) {
      groupedData[type].sentSurveys += sentSurveys
      groupedData[type].responses += responses
    }
  })

  Object.keys(groupedData).forEach((type) => {
    const { sentSurveys, responses } = groupedData[type]
    const rate = sentSurveys > 0 ? (responses / sentSurveys) * 100 : 0
    groupedData[type].responseRate = formatResponseRate(rate)
  })

  if (npsData.sentSurveys.total > 0) {
    const totalRate = (npsData.responses.total / npsData.sentSurveys.total) * 100
    npsData.responseRate.total = formatResponseRate(totalRate)
  }

  return [npsData, groupedData['Single choice'], groupedData['Multiple choice']]
}

export const addPercentSign = (str = '0') => `${str}%`

export const getPropertyFilterOptions = (ids) =>
  ids.map((id) => ({
    id,
    text: `${id}`,
  }))

export const formatAnswerOptionsForTemplate = (options) => {
  return options.map((option, index) => {
    return { id: index + 1, content: option }
  })
}
const prepareBuilding = (buildings, buildingId) => {
  const building = buildings.find((item) => item.id === buildingId)
  return building ? [building] : []
}

const prepareInitialRecipentType = (currentSurvey) => {
  if (currentSurvey?.recipients?.clients) {
    return 'residents'
  } else if (currentSurvey?.recipients?.communityGroups) {
    return 'groups'
  } else if (currentSurvey?.recipients?.clientTags) {
    return 'clientTags'
  } else {
    return 'residents'
  }
}

const prepareInitialResidents = (t, currentSurvey) => {
  if (currentSurvey?.recipients?.clients) {
    return currentSurvey.recipients.clients.map((recipent) => ({
      id: recipent.id,
      value: recipent.id,
      text: recipent.firstName + recipent.lastName,
    }))
  } else if (currentSurvey?.recipients?.residencyStatuses) {
    return currentSurvey.recipients.residencyStatuses.map((status) => {
      const value = getResidentsFilterOptions(t).find((option) => option.value === status)
      return value
    })
  } else {
    return []
  }
}

const prepareInitialGroups = (currentSurvey) => {
  if (currentSurvey?.recipients?.communityGroups) {
    return currentSurvey.recipients.communityGroups.map((group) => ({
      id: group.name,
      value: group.name,
      text: group.name,
    }))
  } else {
    return []
  }
}

const prepareInitialTags = (currentSurvey) => {
  if (currentSurvey?.recipients?.clientTags) {
    return currentSurvey?.recipients?.clientTags.map((tag) => ({
      id: tag.id,
      value: tag.id,
      text: tag.name,
    }))
  } else {
    return []
  }
}

export const prepareInitSurveyFormValues = (t, currentSurvey, buildings, prefilledBuilding) => {
  return {
    buildings: currentSurvey
      ? currentSurvey.buildings || prepareBuilding(buildings, currentSurvey.buildingId)
      : prefilledBuilding,
    questions: currentSurvey ? currentSurvey.questions : [{ title: '', answerOptions: [''] }],
    visibilityPerDay: currentSurvey ? currentSurvey.visibilityPerDay : '',
    visibilityDays: currentSurvey ? currentSurvey.visibilityDays : '',
    startTriggerTime: currentSurvey ? currentSurvey.startTriggerTime : 'before',
    startTriggerDays: currentSurvey ? currentSurvey.startTriggerDays : '1',
    startTriggerType: currentSurvey ? currentSurvey.startTriggerType : 'moveIn',
    socialLinkService: currentSurvey ? currentSurvey.socialLinkService : '',
    replyTo: currentSurvey ? currentSurvey.replyTo || '' : '',
    responseTrigger: currentSurvey ? currentSurvey.responseTrigger : false,
    enablePushNotifications: currentSurvey ? currentSurvey.enablePushNotifications : false,
    startDate: currentSurvey ? currentSurvey.startDate : '',
    endDate: currentSurvey ? currentSurvey.endDate : '',
    startTime: currentSurvey ? parseSurveyStartTime(currentSurvey.startTime) : '',
    subtype: currentSurvey ? currentSurvey.subtype : smileyResponse,
    frequency: currentSurvey
      ? currentSurvey.isOneTime
        ? 'timeBased'
        : 'triggerBased'
      : 'timeBased',
    type: currentSurvey ? currentSurvey.type : satisfactionSurvey,
    reviewVia: 'email',
    yardiPropertyId: currentSurvey ? currentSurvey.yardiPropertyId : '',
    phase: currentSurvey ? currentSurvey.phase : '',
    buildingNumber: currentSurvey ? currentSurvey.buildingNumber : '',
    floor: currentSurvey ? currentSurvey.floor : '',
    communicationType: currentSurvey ? currentSurvey.communicationType : inAppCommunication,
    units: [],
    recipientType: prepareInitialRecipentType(currentSurvey),
    residents: prepareInitialResidents(t, currentSurvey),
    groups: prepareInitialGroups(currentSurvey),
    clientTags: prepareInitialTags(currentSurvey),
  }
}

export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

export const getSurveyFrequencyOptions = (t) => [
  {
    id: 0,
    label: t('surveys:frequencyOptions.timeBased'),
    value: 'timeBased',
  },
  {
    id: 1,
    label: t('surveys:frequencyOptions.triggerBased'),
    value: 'triggerBased',
  },
]

export const timeBasedSurvey = 'timeBased'
export const triggerBasedSurvey = 'triggerBased'

export const getSurveyContinueStartTriggers = (t) => [
  {
    id: 0,
    label: t('surveys:options.beforeTrigger'),
    value: 'before',
  },
  {
    id: 1,
    label: t('surveys:options.afterTrigger'),
    value: 'after',
  },
]

export const getSurveyStartTriggers = (t) => [
  {
    id: 0,
    label: t('surveys:options.moveInDate'),
    value: 'moveIn',
  },
  {
    id: 1,
    label: t('surveys:options.moveOutDate'),
    value: 'moveOut',
  },
  {
    id: 2,
    label: t('surveys:options.leaseStartDate'),
    value: 'leaseStart',
  },
  {
    id: 3,
    label: t('surveys:options.leaseEndDate'),
    value: 'leaseEnd',
  },
  {
    id: 4,
    label: t('surveys:options.afterWorkOrder'),
    value: 'workOrder',
  },
  {
    id: 5,
    label: t('surveys:options.afterReservation'),
    value: 'reservation',
  },
]

export const MAX_DISPLAYS_PER_DAY = 5

export const getRecipientTypesOptions = (t) => [
  { id: 0, label: t('surveys:options.resident'), value: 'residents' },
  { id: 1, label: t('surveys:options.groups'), value: 'groups' },
  { id: 3, label: t('surveys:options.unitTypes'), value: 'units' },
  { id: 4, label: t('surveys:options.residentTagsLabels'), value: 'clientTags' },
]

export const socialLinkServiceOptions = [
  { id: 0, label: 'Google', value: 'google' },
  { id: 1, label: 'Yelp', value: 'yelp' },
  { id: 2, label: 'Apartment.com', value: 'apartments' },
  { id: 3, label: 'Apartment Ratings', value: 'apartmentratings' },
  { id: 4, label: 'Facebook', value: 'facebook' },
]

export const SOCIAL_LINKS_PAGE = '/#/dashboard/building-dashboard/social-links'

export const getId = (arr) => (arr.length ? arr.map(({ id }) => id) : [])

export const preparePreviewBg = (communicationType) => {
  switch (communicationType) {
    case inAppCommunication:
      return '/images/survey-app-preview.png'
    case smsCommunication:
      return '/images/survey-sms-preview.png'
    default:
      return null
  }
}

export const prepareSubtypeIcons = (t, subtype) => {
  const iconSize = 40
  switch (subtype) {
    case smileyResponse:
      return (
        <div className="app-preview__icons">
          <SmileyAngry size={iconSize} />
          <SmileyMeh size={iconSize} />
          <Smiley size={iconSize} />
          <img src="/images/heart-eyes.svg" alt={t('surveys:preview.heartEyesEmojiAlt')} />
        </div>
      )
    case thumbsResponse:
      return (
        <div className="app-preview__icons">
          <ThumbsDown size={iconSize} />
          <ThumbsUp size={iconSize} weight="fill" />
        </div>
      )
    default:
      return (
        <div className="app-preview__icons">
          <Star size={iconSize} weight="fill" />
          <Star size={iconSize} weight="fill" />
          <Star size={iconSize} weight="fill" />
          <Star size={iconSize} weight="fill" />
          <Star size={iconSize} />
        </div>
      )
  }
}

export const getResidentsFilterOptions = (t) => [
  {
    id: 'current',
    text: t('surveys:options.currentResidents'),
    value: 'current',
  },
  {
    id: 'future',
    text: t('surveys:options.futureResidents'),
    value: 'future',
  },
]

export const ADMIN_OR_PM = ['Administrator', 'PropertyManager']
export const FULL_DAY_MINUTES = 1440

export function isCommunicationTypeSms(communicationType) {
  return communicationType === smsCommunication
}

export function isCommunicationTypeEmail(communicationType) {
  return communicationType === emailCommunication
}

export const removeSpaces = (str) => str.replace(/\s+/g, '')

const getReplyCell =
  (index) =>
  ({ row }) => {
    const replies = row && row.original && row.original.replies
    if (replies && replies[index] && replies[index].answer) {
      return replies[index].answer
    }
    return NO_VALUE
  }

export const surveyRecipientsColumns = (t, questions, isInAppCommunication, timezone) => {
  const baseCols = [
    {
      Header: t('surveys:table2.resident'),
      Cell: ({
        row: {
          original: { avatar, name, apartment },
        },
      }) => {
        return (
          <div className="cell-residents">
            <Avatar size="big">
              <img src={avatar} />
            </Avatar>
            <div>
              <p>{name}</p>
              {!!apartment && <span className="cell-residents-apartment">{apartment}</span>}
            </div>
          </div>
        )
      },
    },
    {
      Header: t('surveys:table2.status'),
      Cell: (data) => (
        <div className={`message-status ${data.row.original.status}`}>
          {data.row.original.status}
        </div>
      ),
    },
  ]

  const questionColumns =
    questions.length > 1
      ? questions.map((q, index) => ({
          Header: t('surveys:table2.questionNumber', { number: q.order }),
          Cell: getReplyCell(index),
        }))
      : [
          {
            Header: t('surveys:table2.reply'),
            Cell: getReplyCell(0),
          },
        ]

  const commentColumn = isInAppCommunication
    ? [
        {
          Header: t('surveys:table2.comment'),
          Cell: ({ row }) => {
            const original = row && row.original
            return original && original.comment ? original.comment : NO_VALUE
          },
        },
      ]
    : []

  const updatedAtColumn = [
    {
      Header: t('surveys:table2.lastUpdated'),
      Cell: (data) => {
        const {
          row: {
            original: { updatedAt },
          },
        } = data
        return updatedAt
          ? format(toZonedTime(updatedAt, timezone), 'MM/dd/yyyy hh:mma', { timeZone: timezone })
          : '-'
      },
    },
  ]

  return [...baseCols, ...questionColumns, ...commentColumn, ...updatedAtColumn]
}

export const getSurveyStatusFilterOptions = (t) => [
  {
    id: 0,
    text: t('surveys:options.allStatuses'),
    value: 'all',
  },
  {
    id: 1,
    text: t('surveys:options.sent'),
    value: 'sent',
  },
  {
    id: 2,
    text: t('surveys:options.delivered'),
    value: 'delivered',
  },
  {
    id: 3,
    text: t('surveys:options.opened'),
    value: 'opened',
  },
  {
    id: 4,
    text: t('surveys:options.replied'),
    value: 'replied',
  },
]

export const prepareStatusClassName = (status) =>
  status ? status.split(' ').join('').toLowerCase() : ''

export const prepareAnswers = (t, subtype) => {
  const variants = [
    t('surveys:answers.negative'),
    t('surveys:answers.neutral'),
    t('surveys:answers.good'),
    t('surveys:answers.awesome'),
  ]
  switch (subtype) {
    case smileyResponse:
      return variants
    case thumbsResponse:
      return [t('surveys:answers.negative'), t('surveys:answers.positive')]
    default:
      return [t('surveys:answers.veryNegative'), ...variants]
  }
}

export const NO_VALUE = '-'

export const prepareFrequency = (t, details, timezone) => {
  if (details && timezone) {
    const { startDate, endDate, isOneTime } = details
    const preparedEndDate = endDate ? formatInTimeZone(endDate, timezone, 'MM/dd/yyyy') : ''

    return t('surveys:table2.startEndDate', {
      start: formatInTimeZone(startDate, timezone, 'MM/dd/yyyy'),
      end: isOneTime ? preparedEndDate : t('surveys:columns.ongoing'),
    })
  }
  return NO_VALUE
}

export const prepareRecipients = (t, recipients) => {
  if (!recipients) return NO_VALUE
  const { residencyStatuses, clients, clientTags, communityGroups } = recipients

  let preparedRecipients = []

  if (residencyStatuses && residencyStatuses.length) {
    residencyStatuses.forEach((status) => {
      const currentOption = getResidentsFilterOptions(t).find((option) => option.value === status)
      preparedRecipients.push(
        currentOption ? currentOption.text : t('surveys:recipients.statusResidents', { status }),
      )
    })
  }
  if (clients && clients.length) {
    clients.forEach((client) => {
      preparedRecipients.push(`${client.firstName} ${client.lastName}`)
    })
  }

  if (clientTags && clientTags.length) {
    clientTags.forEach((tag) => {
      preparedRecipients.push(tag.name)
    })
  }

  if (communityGroups && communityGroups.length) {
    communityGroups.forEach((group) => {
      preparedRecipients.push(group.name)
    })
  }

  return preparedRecipients.length ? preparedRecipients.join(', ') : NO_VALUE
}

export const getFrequencyMinMaxTimes = (startDate, timezone) => {
  const toZonedDate = (date) => (date ? toZonedTime(date, timezone) : null)

  const selectedStartDate = toZonedDate(startDate)
  const nowInZone = toZonedDate(new Date())
  const isStartDateToday = selectedStartDate && isSameDay(selectedStartDate, nowInZone)

  const minTime = isStartDateToday
    ? addHours(nowInZone, 2)
    : set(selectedStartDate, { hours: 0, minutes: 0 })

  const maxTime = selectedStartDate ? set(selectedStartDate, { hours: 23, minutes: 45 }) : null

  return {
    minTime,
    maxTime,
  }
}

export const getRecipientsCSVFields = (t, questions, isInAppCommunication, timezone) => {
  return [
    {
      label: t('surveys:table2.resident'),
      value: 'name',
    },
    {
      label: t('surveys:table2.apartment'),
      value: (row) => row.apartment || '-',
    },
    {
      label: t('surveys:table2.status'),
      value: 'status',
    },
    ...(questions.length > 1
      ? questions.map((question, questionIndex) => ({
          label: t('surveys:table2.questionNumber', { number: question.order }),
          value: (row) => row.replies?.[questionIndex]?.answer ?? '-',
        }))
      : [
          {
            label: t('surveys:table2.reply'),
            value: (row) => row.replies?.[0]?.answer ?? '-',
          },
        ]),
    ...(isInAppCommunication
      ? [
          {
            label: t('surveys:table2.comment'),
            value: (row) => row.comment || '-',
          },
        ]
      : []),
    {
      label: t('surveys:table2.lastUpdated'),
      value: (row) =>
        row.updatedAt
          ? format(toZonedTime(row.updatedAt, timezone), 'MM/dd/yyyy hh:mma', {
              timeZone: timezone,
            })
          : '-',
    },
  ]
}
